import { openDB, type IDBPDatabase } from 'idb';
import type {
  Product,
  Inventory,
  Sale,
  OrderType,
  OrderPaymentStatus,
  OrderFulfillmentStatus,
  FulfillmentMethod,
  PaymentMethod,
  OrderDeliveryAddress,
} from '../types';

const DB_NAME = 'pos-offline';
const DB_VERSION = 1;

// Store names
export const STORES = {
  PRODUCTS: 'products',
  INVENTORY: 'inventory',
  SALES_QUEUE: 'salesQueue',
  SYNC_META: 'syncMeta',
} as const;

// Sync metadata type
export interface SyncMetadata {
  key: string;
  lastSyncAt: string;
  status: 'idle' | 'syncing' | 'error';
  errorMessage?: string;
}

// Queued sale type for offline transactions
/** @deprecated Use QueuedOrder instead */
export interface QueuedSale extends Omit<Sale, 'id' | 'createdAt' | 'updatedAt'> {
  queuedAt: string;
  localId: string;
  syncAttempts?: number;
  lastSyncError?: string;
}

/** Queued order item for offline transactions */
export interface QueuedOrderItem {
  productId: string;
  productName?: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

/** Queued order type for offline transactions (replaces QueuedSale) */
export interface QueuedOrder {
  // Local identifiers
  localId: string;
  queuedAt: string;
  syncAttempts?: number;
  lastSyncError?: string;

  // Order type
  orderType: OrderType;

  // Status tracking
  paymentStatus: OrderPaymentStatus;
  fulfillmentStatus: OrderFulfillmentStatus;

  // Location and customer
  locationId: string;
  customerId?: string;
  staffId?: string;

  // Customer information for guest orders
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;

  // Fulfillment
  fulfillmentMethod: FulfillmentMethod;
  deliveryAddress?: OrderDeliveryAddress;

  // Financial fields
  subtotal: number;
  tax: number;
  discount: number;
  totalAmount: number;
  totalItems: number;

  // Payment
  paymentMethod?: PaymentMethod;

  // Items
  items: QueuedOrderItem[];

  // Notes
  notes?: string;
}

// Database schema interface
interface OfflineDB {
  products: {
    key: string;
    value: Product;
    indexes: { locationId: string };
  };
  inventory: {
    key: string;
    value: Inventory;
    indexes: { productId: string; locationId: string };
  };
  salesQueue: {
    key: string;
    value: QueuedOrder;
    indexes: { locationId: string; queuedAt: string };
  };
  syncMeta: {
    key: string;
    value: SyncMetadata;
  };
}

let dbInstance: IDBPDatabase<OfflineDB> | null = null;

/**
 * Initialize and return the IndexedDB database instance
 */
export const getDB = async (): Promise<IDBPDatabase<OfflineDB>> => {
  if (dbInstance) {
    return dbInstance;
  }

  dbInstance = await openDB<OfflineDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Products store
      if (!db.objectStoreNames.contains(STORES.PRODUCTS)) {
        const productsStore = db.createObjectStore(STORES.PRODUCTS, {
          keyPath: 'id',
        });
        productsStore.createIndex('locationId', 'locationId', { unique: false });
      }

      // Inventory store
      if (!db.objectStoreNames.contains(STORES.INVENTORY)) {
        const inventoryStore = db.createObjectStore(STORES.INVENTORY, {
          keyPath: 'id',
        });
        inventoryStore.createIndex('productId', 'productId', { unique: false });
        inventoryStore.createIndex('locationId', 'locationId', { unique: false });
      }

      // Sales queue store
      if (!db.objectStoreNames.contains(STORES.SALES_QUEUE)) {
        const salesQueueStore = db.createObjectStore(STORES.SALES_QUEUE, {
          keyPath: 'localId',
        });
        salesQueueStore.createIndex('locationId', 'locationId', { unique: false });
        salesQueueStore.createIndex('queuedAt', 'queuedAt', { unique: false });
      }

      // Sync metadata store
      if (!db.objectStoreNames.contains(STORES.SYNC_META)) {
        db.createObjectStore(STORES.SYNC_META, {
          keyPath: 'key',
        });
      }
    },
  });

  return dbInstance;
};

/**
 * Close the database connection
 */
export const closeDB = async (): Promise<void> => {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
};

/**
 * Clear all data from the database
 */
export const clearDB = async (): Promise<void> => {
  const db = await getDB();
  const tx = db.transaction(
    [STORES.PRODUCTS, STORES.INVENTORY, STORES.SALES_QUEUE, STORES.SYNC_META],
    'readwrite'
  );

  await Promise.all([
    tx.objectStore(STORES.PRODUCTS).clear(),
    tx.objectStore(STORES.INVENTORY).clear(),
    tx.objectStore(STORES.SALES_QUEUE).clear(),
    tx.objectStore(STORES.SYNC_META).clear(),
  ]);

  await tx.done;
};

// ============================================
// Product Catalog Caching Functions
// ============================================

/**
 * Save products to the products store
 */
export const saveProducts = async (products: Product[], locationId?: string): Promise<void> => {
  const db = await getDB();
  const tx = db.transaction(STORES.PRODUCTS, 'readwrite');
  const store = tx.objectStore(STORES.PRODUCTS);

  // Filter products by locationId if provided
  const productsToSave = locationId
    ? products.filter(p => p.locationId === locationId)
    : products;

  // Save each product
  await Promise.all(
    productsToSave.map(product => store.put(product))
  );

  await tx.done;
};

/**
 * Get products from the products store
 */
export const getProducts = async (locationId?: string): Promise<Product[]> => {
  const db = await getDB();

  if (locationId) {
    // Use index to filter by locationId
    return await db.getAllFromIndex(STORES.PRODUCTS, 'locationId', locationId);
  }

  // Get all products if no locationId specified
  return await db.getAll(STORES.PRODUCTS);
};

/**
 * Clear all products from the products store
 */
export const clearProducts = async (): Promise<void> => {
  const db = await getDB();
  await db.clear(STORES.PRODUCTS);
};

// ============================================
// Inventory Caching Functions
// ============================================

/**
 * Save inventory items to the inventory store
 */
export const saveInventory = async (inventory: Inventory[], locationId?: string): Promise<void> => {
  const db = await getDB();
  const tx = db.transaction(STORES.INVENTORY, 'readwrite');
  const store = tx.objectStore(STORES.INVENTORY);

  // Filter inventory by locationId if provided
  const inventoryToSave = locationId
    ? inventory.filter(i => i.locationId === locationId)
    : inventory;

  // Save each inventory item
  await Promise.all(
    inventoryToSave.map(item => store.put(item))
  );

  await tx.done;
};

/**
 * Get inventory items from the inventory store
 */
export const getInventory = async (locationId?: string): Promise<Inventory[]> => {
  const db = await getDB();

  if (locationId) {
    // Use index to filter by locationId
    return await db.getAllFromIndex(STORES.INVENTORY, 'locationId', locationId);
  }

  // Get all inventory if no locationId specified
  return await db.getAll(STORES.INVENTORY);
};

/**
 * Clear all inventory from the inventory store
 */
export const clearInventory = async (): Promise<void> => {
  const db = await getDB();
  await db.clear(STORES.INVENTORY);
};

const dbApi = {
  getDB,
  closeDB,
  clearDB,
  STORES,
  saveProducts,
  getProducts,
  clearProducts,
  saveInventory,
  getInventory,
  clearInventory,
};

export default dbApi;
